import { getAPIClient } from "../helpers";
import type {
  ReservationRequestFilters,
  UpdateReservationRequestData,
} from "../../api/routes/shop/routes/reservation_requests";

export const reservationRequestsAPI = {
  // GET /reservation_requests/pending-count - Get number of pending reservation requests
  getPendingReservationRequestsCount: async () => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservation_requests["pending-count"].$get();

    if (!resp.ok) {
      throw new Error(
        `Failed to fetch pending reservation requests count: ${resp.status}`,
      );
    }

    const body = await resp.json();
    return (body as { data?: { count?: number } }).data?.count ?? 0;
  },

  // GET /reservation_requests - List reservation requests with filtering
  getReservationRequests: async (
    filters?: ReservationRequestFilters,
    signal?: AbortSignal,
  ) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservation_requests.$get(
      {
        query: filters || {},
      },
      signal ? { init: { signal } } : undefined,
    );

    if (!resp.ok) {
      throw new Error(`Failed to fetch reservation requests: ${resp.status}`);
    }

    return await resp.json();
  },

  // GET /reservation_requests/:id - Get single reservation request
  getReservationRequest: async (id: string) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservation_requests[":id"].$get({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error(`Failed to fetch reservation request: ${resp.status}`);
    }

    return await resp.json();
  },

  // POST /reservation_requests - Create new reservation request
  createReservationRequest: async (data: any) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservation_requests.$post({
      json: data,
    });

    if (!resp.ok) {
      throw new Error(`Failed to create reservation request: ${resp.status}`);
    }

    return await resp.json();
  },

  // PUT /reservation_requests/:id - Update reservation request
  updateReservationRequest: async (
    id: string,
    data: UpdateReservationRequestData,
  ) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservation_requests[":id"].$put({
      param: { id },
      json: data,
    });

    if (!resp.ok) {
      throw new Error(`Failed to update reservation request: ${resp.status}`);
    }

    return await resp.json();
  },

  // PATCH /reservation_requests/:id/status - Update reservation request status (for reject)
  updateReservationRequestStatus: async (id: string, statusUpdate: any) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservation_requests[":id"].status.$patch({
      param: { id },
      json: statusUpdate,
    });

    if (!resp.ok) {
      throw new Error(
        `Failed to update reservation request status: ${resp.status}`,
      );
    }

    return await resp.json();
  },

  // POST /reservation_requests/:id/accept - Accept request and create reservation
  acceptReservationRequest: async (
    id: string,
    data: { table_ids?: number[]; duration_mins?: number },
  ) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservation_requests[":id"].accept.$post({
      param: { id },
      json: data,
    });

    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string }).error ||
          `Failed to accept reservation request: ${resp.status}`,
      );
    }

    return await resp.json();
  },

  // DELETE /reservation_requests/:id - Delete reservation request
  deleteReservationRequest: async (id: string) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.reservation_requests[":id"].$delete({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error(`Failed to delete reservation request: ${resp.status}`);
    }

    return await resp.json();
  },
};
